import React from 'react';
import { Music } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useShowTracks, useTrackCover } from '@/hooks/useShowTracks';
import SaveSongMenu from '@/components/yakaleo/SaveSongMenu';

function formatPlayedAt(playedAt, lang) {
  if (!playedAt) return '';
  return new Date(playedAt).toLocaleTimeString(
    lang === 'es' ? 'es-ES' : 'en-US',
    { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }
  );
}

function TrackRow({ track, dark, showSaveMenu }) {
  const { lang } = useI18n();
  const cover = useTrackCover(track);
  const time = formatPlayedAt(track.played_at, lang);
  const textClass = dark ? 'text-white' : 'text-black';
  const subClass = dark ? 'text-white/50' : 'text-neutral-500';

  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="w-10 h-10 rounded overflow-hidden bg-neutral-200 flex-shrink-0">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music size={14} className="text-neutral-400" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`${textClass} text-sm font-bold truncate`}>{track.title}</p>
        <p className={`${subClass} text-xs truncate`}>{track.artist}</p>
      </div>
      <span className={`${subClass} text-xs flex-shrink-0 tabular-nums`}>{time}</span>
      {showSaveMenu && (
        <SaveSongMenu
          title={track.title}
          artist={track.artist}
          dark={dark}
          className="flex-shrink-0"
        />
      )}
    </div>
  );
}

/**
 * Live tracklist for a show.
 *
 * - variant="full" (default): light-themed, for the show page.
 * - variant="compact": dark-themed, limited to 5 rows, for under the on-air card.
 *
 * When isLive is true, shows today's tracks under "Tracklist" / "Lista de
 * canciones". When not live, shows the most recent episode under "Last
 * episode" / "Último episodio".
 */
export default function Tracklist({ show, isLive, variant = 'full' }) {
  const { lang } = useI18n();
  const es = lang === 'es';
  const { tracks, loading } = useShowTracks(show?.id, isLive);
  const confident = tracks.filter((t) => t.confident);

  if (loading && !confident.length) return null;
  if (!confident.length) return null;

  const heading = isLive
    ? es ? 'Lista de canciones' : 'Tracklist'
    : es ? 'Último episodio' : 'Last episode';

  const dark = variant === 'compact';
  const limited = dark ? confident.slice(0, 5) : confident;

  if (dark) {
    return (
      <div className="mb-8">
        <h3 className="text-white/60 text-xs font-bold tracking-widest uppercase mb-3">{heading}</h3>
        <div className="divide-y divide-white/5">
          {limited.map((t) => (
            <TrackRow key={t.id} track={t} dark showSaveMenu />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h2 className="text-black text-sm font-black tracking-widest uppercase mb-4">{heading}</h2>
      <div className="bg-white rounded-xl border border-neutral-100 px-4 divide-y divide-neutral-100">
        {limited.map((t) => (
          <TrackRow key={t.id} track={t} dark={false} showSaveMenu />
        ))}
      </div>
    </div>
  );
}